import * as Sentry from '@sentry/react';
import type { HostedReport } from './types/report.types';

/**
 * Error reporting for the hosted report.
 *
 * Configured at BUILD time (`REACT_APP_SENTRY_DSN`), not from `/public-config`
 * like the other frontends: on an agency's own hostname the Worker forwards
 * nothing but `/api/public/reports/`, so a runtime config fetch would get
 * `index.html` back and Sentry would silently never start. CRA writes the
 * value into the bundle, so every hostname has it without a request.
 *
 * No DSN, no Sentry: local development and the test runner go without.
 */

/**
 * How much of a token an event may carry.
 *
 * The token IS the authorization -- whoever has it can open the report -- so
 * it never reaches Sentry whole. Eight hex characters find the delivery
 * (`shareToken` is indexed, and a prefix match uses the index) and leave 96
 * random bits nobody holding the event can guess.
 */
const TOKEN_PREFIX_CHARS = 8;

/** A token in a page path or in the one API path this app calls. */
const TOKEN_IN_PATH =
  /(\/report\/(?:preview\/)?|\/api\/public\/reports\/(?:preview\/)?)([^/?#\s"'&]+)/g;

export function tokenPrefix(token: string): string {
  return token.slice(0, TOKEN_PREFIX_CHARS);
}

/** Every token in `text` cut down to its prefix. */
export function scrubReportTokens(text: string): string {
  return text.replace(
    TOKEN_IN_PATH,
    (_match, path: string, token: string) =>
      `${path}${tokenPrefix(token)}[redacted]`,
  );
}

/**
 * The whole event, every string in it scrubbed.
 *
 * Walked rather than field by field because the token turns up in more places
 * than anyone will list: the page URL, the Referer header, navigation and XHR
 * breadcrumbs, and an error message that quotes a URL. A field added by a
 * later SDK version is covered without anyone remembering it.
 */
export function scrubDeep<T>(value: T): T {
  if (typeof value === 'string') return scrubReportTokens(value) as T;
  if (Array.isArray(value)) return value.map(item => scrubDeep(item)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = scrubDeep(item);
    }
    return out as T;
  }
  return value;
}

/** Starts Sentry when the build carried a DSN. Answers whether it did. */
export function initSentry(): boolean {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    // The reader is an agency's client with no account here. Nothing about
    // them -- IP address included -- is ours to collect.
    sendDefaultPii: false,
    beforeSend: event => scrubDeep(event),
    beforeBreadcrumb: breadcrumb => scrubDeep(breadcrumb),
  });

  // Which agency domain the reader was on. Set once, here, so a crash before
  // the report has even loaded still says where it happened.
  Sentry.setTag('report_host', window.location.hostname);
  return true;
}

/**
 * Which report this page is showing, known from the URL alone.
 *
 * Called before the fetch, so a failure to load still names the report. Also
 * clears what a previous week left: the week navigator swaps the token
 * without reloading the page.
 */
export function tagReportRoute(token: string, preview: boolean): void {
  const prefix = tokenPrefix(token);
  Sentry.setTags({
    report_kind: preview ? 'preview' : 'shared',
    report_token_prefix: prefix,
    report_company: undefined,
    report_agency: undefined,
  });
  Sentry.setUser({ id: `report:${prefix}` });
  Sentry.setContext('report', null);
}

/**
 * Who the report is for, once it has loaded.
 *
 * There is no signed-in user to name -- the reader came from an email link --
 * so the Sentry "user" is the report: the business it is about and the agency
 * that sent it. Business listing data, nothing about the person reading.
 */
export function tagLoadedReport(report: HostedReport, token: string): void {
  const company = report.company?.name || null;
  const agency = report.branding?.brandName || null;
  Sentry.setTags({
    report_company: company ?? undefined,
    report_agency: agency ?? undefined,
  });
  Sentry.setUser({
    id: `report:${tokenPrefix(token)}`,
    username: company ?? undefined,
  });
  Sentry.setContext('report', {
    company,
    city: report.company?.city ?? null,
    state: report.company?.state ?? null,
    agency,
    version: report.version,
    status: report.status,
    sentAt: report.sentAt,
    periodStart: report.period?.start ?? null,
    periodEnd: report.period?.end ?? null,
    isTest: report.isTest,
    isPreview: !!report.isPreview,
  });
}

/**
 * A report that did not load.
 *
 * A 404 is the expected answer for a wrong, expired or other-agency link, and
 * the page shows the same message for all of them on purpose; filing those
 * would bury the failures that matter. Anything else -- a 5xx, the Worker's
 * upstream fetch failing, the network dropping -- is a reader who should have
 * seen a report and got told it does not exist.
 */
export function reportLoadFailure(error: unknown): void {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  if (status === 404) return;
  Sentry.captureException(error, {
    tags: {
      report_load_failed: 'true',
      http_status: status ? String(status) : 'none',
    },
  });
}
